import React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { PrimaryHeader, PrimaryHeaderDataProps } from './primaryHeader/PrimaryHeader';
import { SecondaryHeader, SecondaryHeaderDataProps } from './secondaryHeader/SecondaryHeader';
import { AnimationDirection } from './menuAnimations.util';
import { MontiAnchor } from './MontiAnchor';
import { ErrorBoundary } from '../../../errorHandling/ErrorBoundary';
import { getHeaderHeights } from './header.utils';
import { MEDIA_BREAKPOINTS } from '../../../../mediaQueries.const';
import { HeaderAnimation, HeaderAnimationContext } from './HeaderAnimation';

const getStyle = (animationDuration: number) => {
  return StyleSheet.create({
    header: {
      position: 'fixed',
      width: '100%',
      zIndex: 1500,
      top: 0,
    },
    foldingUp: {
      [MEDIA_BREAKPOINTS.medium]: {
        transition: `${animationDuration}ms`,
        transform: 'translateY(-100%)',
        transitionDelay: `${animationDuration}ms`,
      },
      [MEDIA_BREAKPOINTS.small]: {
        transition: `${animationDuration}ms`,
        transform: 'translateY(-100%)',
        transitionDelay: `${animationDuration}ms`,
      },
    },
    foldingDown: {
      [MEDIA_BREAKPOINTS.medium]: {
        transform: 'translateY(0)',
      },
      [MEDIA_BREAKPOINTS.small]: {
        transform: 'translateY(0)',
      },
    },
  });
};

export interface HeaderLinksDataProps {
  href: string;
  text: string;
  children: HeaderLinksDataProps[];
}

export interface HeaderDataProps {
  primaryHeader: PrimaryHeaderDataProps;
  secondaryHeader: SecondaryHeaderDataProps | null;
}

export const HeaderComponent: React.FunctionComponent<HeaderDataProps> = props => {
  const { menuFoldState, animationDuration } = React.useContext<HeaderAnimationContext>(HeaderAnimationContext);
  const { primaryHeader, secondaryHeader } = props;
  const { links, logo, editions, cta, menu, videoLogo, pagePath, editionEndpoint } = primaryHeader;

  const generateSecondaryHeader = () => {
    if (!secondaryHeader) {
      return null;
    }
    return (
      <SecondaryHeader
        moreLinksText={secondaryHeader.moreLinksText}
        links={secondaryHeader.links}
        logo={secondaryHeader.logo}
        pagePath={secondaryHeader.pagePath}
        animation={menuFoldState}
        animationDuration={animationDuration}
      />
    );
  };

  const getHeaderStyleWithAnimation = () => {
    const style = getStyle(animationDuration);

    if (menuFoldState === AnimationDirection.NONE) {
      return css(style.header);
    }

    const animationStyle = menuFoldState === AnimationDirection.UP ? style.foldingUp : style.foldingDown;
    return css(style.header, animationStyle);
  };

  return (
    <ErrorBoundary>
      <MontiAnchor isSecondaryHeaderEnabled={!!secondaryHeader} menuAnimation={menuFoldState} />
      <header className={getHeaderStyleWithAnimation()}>
        <PrimaryHeader {...{ links, logo, editions, cta, menu, videoLogo, pagePath, editionEndpoint, menuAnimation: menuFoldState, reduceHeightAnimationDuration: animationDuration }} />
        {generateSecondaryHeader()}
      </header>
    </ErrorBoundary>
  );
};

export const Header: React.FunctionComponent<HeaderDataProps> = props => {
  const { secondaryHeader } = props;
  const headerHeights = getHeaderHeights(secondaryHeader !== null);
  return (
    <ErrorBoundary>
      <HeaderAnimation headerHeights={headerHeights}>
        <HeaderComponent {...props} />
      </HeaderAnimation>
    </ErrorBoundary>
  );
};
